// components/ui/Sidebar.tsx
import React from 'react';
import { 
  ChevronLeft,
  ChevronRight,
  X,
  PlusCircle
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
  badge?: number;
  isVisible?: boolean;
  permission?: string;
}

export interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

export interface SidebarConfig {
  sections: SidebarSection[];
  username: string;
  logoUrl?: string;
  showModulesCTA?: boolean;
  onActivateModules?: () => void;
}

interface SidebarProps {
  config: SidebarConfig;
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  customClassName?: string;
}

const DashboardSidebar: React.FC<SidebarProps> = ({
  config,
  isCollapsed,
  onToggle,
  isMobile = false,
  customClassName = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  const showLabel = !isCollapsed || isMobile;

  const renderMenuItem = (item: SidebarItem) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    if (item.isVisible === false) return null;

    return (
      <button
        key={item.path}
        onClick={() => handleNavigation(item.path)}
        className={`
          w-full flex items-center rounded-lg p-3 transition-colors mb-1
          ${active 
            ? 'bg-primary text-white shadow-sm' 
            : 'text-gray-600 hover:bg-gray-100'
          }
          ${showLabel ? 'justify-start' : 'justify-center'}
          group relative
        `}
        title={!showLabel ? item.label : undefined}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {showLabel && (
          <>
            <span className="ml-3 font-medium flex-1 text-left">
              {item.label}
            </span>
            {item.badge && (
              <span className={`
                px-2 py-1 text-xs rounded-full
                ${active 
                  ? 'bg-primary/80 text-white' 
                  : 'bg-gray-200 text-gray-800'
                }
              `}>
                {item.badge}
              </span>
            )}
          </>
        )}
        
        {!showLabel && !isMobile && (
          <div className="
            absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded
            opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity
            whitespace-nowrap z-50
          ">
            {item.label}
          </div>
        )}
      </button>
    );
  };

  const renderSection = (section: SidebarSection, index: number) => {
    const visibleItems = section.items.filter(item => item.isVisible !== false);
    if (visibleItems.length === 0) return null;

    return (
      <div key={index} className="mb-6">
        {showLabel && section.title && (
          <div className="mb-2 px-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {section.title}
            </span>
          </div>
        )}
        <div className="space-y-1">
          {visibleItems.map(renderMenuItem)}
        </div>
      </div>
    );
  };

  return (
    <div className={`
      h-full bg-white border-r border-gray-200 flex flex-col
      ${isMobile ? 'w-64' : isCollapsed ? 'w-20' : 'w-64'}
      transition-all duration-200 ${customClassName}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {config.logoUrl ? (
              <img src={config.logoUrl} alt={config.username} className="w-8 h-8 rounded-lg flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-orange-400 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {config.username.slice(0, 2).toUpperCase()}
              </div>
            )}
            <span className="font-bold text-gray-900 truncate">{config.username}</span>
          </div>
        )}
        
        {isCollapsed && !isMobile && (
          <div className="w-full flex justify-center">
            {config.logoUrl ? (
              <img src={config.logoUrl} alt={config?.username} className="w-8 h-8 rounded-lg" />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-orange-400 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                {config.username?.slice(0, 2)?.toUpperCase()}
              </div>
            )}
          </div>
        )}
        
        <button
          onClick={onToggle}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 flex-shrink-0"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isMobile ? (
            <X className="w-4 h-4 text-gray-600" />
          ) : isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden p-4">
        {config.sections.map((section, index) => renderSection(section, index))}
        
        {config.showModulesCTA && config.onActivateModules && (
          <div className="mt-auto pt-4">
            <div className="p-4 bg-gradient-to-r from-primary/5 to-orange-50 rounded-lg border border-primary/20">
              <PlusCircle className="w-8 h-8 text-primary mx-auto mb-2" />
              {showLabel && (
                <>
                  <h3 className="font-semibold text-gray-900 text-center mb-1">
                    Activer des modules
                  </h3>
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Aucun module n'est actif. Activez des modules pour débloquer les fonctionnalités.
                  </p>
                </>
              )}
              <button
                onClick={config.onActivateModules}
                className="w-full bg-primary text-white rounded-lg py-2 px-4 font-medium hover:bg-primary/90 transition-colors text-sm"
              >
                {showLabel ? 'Configurer les modules' : <PlusCircle className="w-5 h-5 mx-auto" />}
              </button>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default DashboardSidebar;